import { Component, Input, OnChanges, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { HisCoreSharedModule } from '../../../../../../core/his-core-shared.module';
import { HisUiService } from '../../../../../../core/service/base/his-ui.service';
import { HisTableConfigResults } from '../../../../../../core/component/table-config/his-table-config.component';
import { HisReorderTableService } from '../../../../../../core/service/base/his-reorder-table.service';
import { isEmpty, startsWith, toLower } from 'lodash';
import { Column, SelectableColumn } from '../model/column';
import { ColumnType } from '../model/column-type';
import { Row } from '../model/row';
import { TableHeader } from '../model/table-header';
import { Result } from '../model/result';
import { SortEvent, PrimeTemplate } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TableToolbarComponent } from '../../smart-tree-table/table-toolbar/table-toolbar.component';
import { BiaColumnFormatPipe } from '../pipe/bia-columnformat.pipe';

@Component( {
    standalone: true,
    selector: 'his-bi-flat-table',
    templateUrl: './bianalysis-flat-table.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TableModule, HisCoreSharedModule, PrimeTemplate, BiaColumnFormatPipe]
} )
export class BiAnalysisFlatTableComponent implements OnChanges {
    private hisUi = inject( HisUiService );
    hisReorderTableService = inject( HisReorderTableService );

    @Input() headerOrder: string[] = [];
    @Input() totalColumn: number = 0;
    @Input( { required: true } ) loading!: boolean;

    columns: Column[] = [];
    selectableColumns: SelectableColumn[] = [];
    rows: any[] = [];
    columnType = ColumnType;
    tableHeader: TableHeader | null = null;
    flatTable: Result | null = null;
    headerHierarchy: Column[] = [];
    filterActive = true;
    isReorderable = false;
    frozenCol: string = 'noneCol';

    @Input()
    set result( result: Result ) {
        this.flatTable = Result.resultFactory( result );
        this.columns = result.columns;
        this.columns.forEach( c => c.title = c.header );
        this.selectableColumns = this.columns.map( column => ( { ...column, checked: true } ) );
        this.filterActive = false;
        this.columns = this.flatTable.getVisibleColumns();
        this.tableHeader = this.flatTable.generateHierarchyHeader();
        this.rows = this.flatTable.rows.filter( row => !this.isEmptyRow( row.cells ) ).map( row => row.cells );
    }

    uiPipe( token: string ): string {
        return this.hisUi.translate( token );
    }

    isEmptyRow( row: Row[] ): boolean {
        let empty = true;
        this.columns.forEach( col => {
            if ( empty ) {
                if ( row[col.field as any] != null ) {
                    empty = isEmpty( row[col.field as any].toString() );
                }
            }
        } );
        return empty;
    }

    isDecimalColumn( columnType: ColumnType ): boolean {
        return columnType === ColumnType.DecimalColumn;
    }

    rearrangeHeader( headerOrder: string[] ) {
        if ( this.flatTable === null ) {
            return;
        }
        this.flatTable.headerOrder = headerOrder;
        this.tableHeader = this.flatTable.generateHierarchyHeader();
        let numberOfHeaderRows = this.tableHeader.headerRows.length;
        this.columns = this.flatTable.getAttributeColumns().concat(
            this.tableHeader.headerRows[numberOfHeaderRows - 1].headerElements.map( element => element.column ) )
            .filter( this.flatTable.onlyUnique );
    }

    customSort( event: SortEvent ) {
        console.log( 'SortEvent: ', event );
        let fields: string[] = [];
        let orderFns: ( ( a: any, b: any ) => number )[] = [];
        let orders: number[] = [];
        if ( event.mode === 'multiple' ) {
            event.multiSortMeta!.forEach( meta => {
                let field = meta.field;
                let col = this.columns.find(
                    ( c: Column ) => c.field === field
                );
                if ( col ) {
                    fields.push( col.field );
                    orderFns.push( this.getOrderFn( col.type ) );
                    orders.push( meta.order );
                }
            } );
        } else {
            let col = this.columns.find(
                ( c: Column ) => c.field === event.field
            );
            if ( col ) {
                fields.push( col.field );
                orderFns.push( this.getOrderFn( col.type ) );
                orders.push( event.order! );
            }
        }
        this.sort( event.data!, fields, orders, orderFns );
    }

    getOrderFn( type: ColumnType ): ( ( a: any, b: any ) => number ) {
        if ( type === ColumnType.DateColumn ) {
            return ( a, b ) => {
                if ( a === b ) return 0;
                if ( a === null || b === null ) return this.nullOrder( a );
                let ma = this.millisFromDate( a );
                let mb = this.millisFromDate( b );
                return ma - mb;
            };
        } else if ( type === ColumnType.DecimalColumn || type === ColumnType.IntegerColumn ) {
            return ( a, b ) => {
                if ( a === b ) return 0;
                if ( a === null || b === null ) return this.nullOrder( a );
                return a - b;
            };
        }
        return ( a, b ) => {
            if ( a === b ) return 0;
            if ( a === null || b === null ) return this.nullOrder( a );
            return a.toString().localeCompare( b.toString(), 'de' );
        };
    }

    nullOrder( a: any ): number {
        if ( a === null ) {
            return -1;
        }
        return 1;
    }

    sort( data: any[], fields: string[], orders: number[], orderFns: ( ( a: any, b: any ) => number )[] ) {
        data.sort( ( a, b ) => {
            let result = 0;
            let index = 0;
            while ( result === 0 && index < fields.length ) {
                let va = a[fields[index]];
                let vb = b[fields[index]];
                let order = orders[index];
                let orderFn = orderFns[index];
                result = order * orderFn( va, vb );
                index += 1;
            }
            return result;
        } );
    }

    millisFromDate( d: string ): number {
        let parts = d.split( '.' );
        let dIso = parts[2] + '-' + parts[1] + '-' + parts[0];
        return Date.parse( dIso );
    }

    refreshTableColumns( config: HisTableConfigResults ) {
        this.columns = config.columResults;
        this.isReorderable = config.isReorderable;
        this.frozenCol = config.frozenCol;
    }

    ngOnChanges( changes: SimpleChanges ): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const headerOrderChange = changes['headerOrder'];
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const totalColumnChange = changes['totalColumn'];

        if ( headerOrderChange ) {
            this.rearrangeHeader( headerOrderChange.currentValue );
        }

        if ( totalColumnChange ) {
            this.setTotalColumn();
        }
    }

    setTotalColumn() {
        if ( this.flatTable === null ) {
            throw new Error( 'Cannot rearrange header. Report does not exist.' );
        }
        let totalColumns = this.flatTable?.columns.filter( col => col.isTotalColumn === true ) ?? [];
        for ( let totalColumn of totalColumns ) {
            totalColumn.hidden = !this.totalColumn;
        }
        this.rearrangeHeader( this.headerOrder );
    }

}
